import { opportunityService } from '@/services/opportunity.service';
import axios from 'axios';

const initialState = () => ({
  opportunity: null,
  // Set while an opportunity loads: until it arrives, `opportunity` is still the one opened before.
  pendingId: null,
  status: {
    error: null,
    pending: false,
    pendingEdit: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

// Loads can overlap. A navigation (foreground load) is the user's target: only
// a newer navigation may supersede it. A silent background refresh never
// supersedes anything; its response is dropped if a navigation started after
// it, or if the store no longer holds (or is no longer about to hold) the
// opportunity it refreshed — so a slow refresh of A cannot replace B, and a
// refresh of A cannot discard the navigation to B in progress.
let navigationSeq = 0;

// The API sends an opportunity's documents in no particular order; every list of them shows them by name.
const byName = (a, b) => a.name.localeCompare(b.name);

const withDocumentsByName = (opportunity) =>
  opportunity.documents ? { ...opportunity, documents: [...opportunity.documents].sort(byName) } : opportunity;

const actions = {
  getOpportunity({ commit, state }, { id, cancelToken, silent = false }) {
    if (!silent) navigationSeq += 1;
    const startedAt = navigationSeq;
    const superseded = () => {
      if (startedAt !== navigationSeq) return true;
      if (!silent) return false;
      const targetId = state.pendingId || (state.opportunity && state.opportunity.id);
      return targetId !== id;
    };

    if (!silent) {
      commit('GET_OPPORTUNITY', id);
    }

    return opportunityService.getOpportunity({ id, cancelToken }).then(
      (opportunity) => {
        // Callers still get the data; only the shared state is left alone.
        if (superseded()) return opportunity;
        commit('SUCCESS', opportunity);
        return opportunity;
      },
      (error) => {
        if (!silent) commit('GET_OPPORTUNITY_FAILED', id);
        if (!axios.isCancel(error)) {
          if (!superseded()) commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  deleteOpportunity({ commit }, { opportunity }) {
    commit('DELETE_OPPORTUNITY');
    const id = opportunity.id;
    const tenderId = opportunity.tenderId;

    return opportunityService.deleteOpportunity({ id }).then(
      () => {
        commit('DELETE_OPPORTUNITY_SUCCESS');
        commit('opportunities/OPPORTUNITY_DELETE_SUCCESS', id, { root: true });
        if (tenderId) commit('tenders/SUCCESS_DELETING_OPPORTUNITY', tenderId, { root: true });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  editOpportunity({ commit }, { opportunityId, opportunity }) {
    commit('EDIT_OPPORTUNITY');

    return opportunityService.editOpportunity({ opportunityId, opportunity }).then(
      (opportunity) => {
        commit('opportunities/EDIT_OPPORTUNITY_SUCCESS', opportunity, { root: true });
        commit('EDIT_OPPORTUNITY_SUCCESS', opportunity);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },

  assign({ commit, dispatch, rootGetters }, { opportunityId, userIds }) {
    return opportunityService.assign({ opportunityId, userIds }).then(
      (response) => {
        commit('opportunities/UPDATE_ASSIGNEES', { opportunityId, assignees: response }, { root: true });
        commit('UPDATE_ASSIGNEES', { opportunityId, assignees: response });

        const currentUser = rootGetters['auth/currentUser'];
        if (currentUser && userIds.includes(currentUser.id)) {
          // Self-assignment creates an opportunity-assignations event; pull it in now.
          dispatch('notifications/getEvents', null, { root: true });
        }
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },

  assignLabels({ commit }, { opportunityId, labelIds }) {
    return opportunityService.assignLabels({ opportunityId, labelIds }).then(
      (response) => {
        commit('opportunities/UPDATE_LABELS', { opportunityId, labels: response }, { root: true });
        return commit('UPDATE_LABELS', { opportunityId, labels: response });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },

  shareOpportunity({ commit }, { id, recipientIds }) {
    commit('SHARING_OPPORTUNITY');

    return opportunityService
      .share({
        id,
        recipientIds,
      })
      .then(
        () => {
          commit('SHARING_OPPORTUNITY_SUCCESS');
        },
        (error) => {
          if (!axios.isCancel(error)) {
            commit('ERROR', error);
            throw error;
          }
        },
      );
  },
};

const mutations = {
  GET_OPPORTUNITY(state, id) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.pendingId = id;
  },
  // Cancelled too. The opportunity held is left whole, as the pages don't load an opportunity they already hold again.
  GET_OPPORTUNITY_FAILED(state, id) {
    if (state.pendingId === id) state.pendingId = null;
  },
  EDIT_OPPORTUNITY(state) {
    state.status = {
      error: null,
      pending: false,
      pendingEdit: true,
      resolved: false,
      rejected: false,
    };
  },
  EDIT_OPPORTUNITY_SUCCESS(state, opportunity) {
    state.status = {
      ...state.status,
      pendingEdit: false,
      resolved: true,
    };
    const previousAssignees = state.opportunity && state.opportunity.assignees;
    const previousLabels = state.opportunity && state.opportunity.labels;
    state.opportunity = withDocumentsByName({
      ...opportunity,
      assignees: opportunity.assignees || previousAssignees,
      labels: opportunity.labels || previousLabels,
    });
  },
  // Committed from the go-nogo module so the header updates without re-fetching the opportunity.
  SET_FINAL_DECISION(state, { opportunityId, finalDecision }) {
    if (!state.opportunity || state.opportunity.id !== opportunityId) return;
    state.opportunity = { ...state.opportunity, finalDecision };
  },
  UPDATE_ASSIGNEES(state, { opportunityId, assignees }) {
    if (!state.opportunity || state.opportunity.id !== opportunityId) return;
    state.opportunity = { ...state.opportunity, assignees };
  },
  UPDATE_LABELS(state, { opportunityId, labels }) {
    if (!state.opportunity || state.opportunity.id !== opportunityId) return;
    state.opportunity = { ...state.opportunity, labels };
  },
  // Committed from the opportunityDocuments module, whose list of the opened opportunity's documents is this one.
  SUCCESS_ADDING_DOCUMENTS(state, { opportunityId, documents }) {
    if (!state.opportunity || state.opportunity.id !== opportunityId || !state.opportunity.documents) return;
    const existingIds = new Set(state.opportunity.documents.map((d) => d.id));
    state.opportunity.documents.push(...documents.filter((d) => !existingIds.has(d.id)));
    state.opportunity.documents.sort(byName);
  },
  SUCCESS_DELETING_DOCUMENT(state, { opportunityId, documentId }) {
    if (!state.opportunity || state.opportunity.id !== opportunityId || !state.opportunity.documents) return;
    const documentIndex = state.opportunity.documents.findIndex((d) => d.id === documentId);
    if (documentIndex > -1) state.opportunity.documents.splice(documentIndex, 1);
  },
  SUCCESS(state, opportunity) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.opportunity = withDocumentsByName(opportunity);
    if (state.pendingId === opportunity.id) state.pendingId = null;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  DELETE_OPPORTUNITY(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  DELETE_OPPORTUNITY_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  SHARING_OPPORTUNITY(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SHARING_OPPORTUNITY_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
};

const getters = {
  opportunity: (state) => state.opportunity,
  // None while another opportunity loads: those held until then are the previous one's.
  documents: (state) => {
    if (!state.opportunity || (state.pendingId && state.pendingId !== state.opportunity.id)) return [];
    return state.opportunity.documents || [];
  },

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const opportunity = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
